// src/pages/ChangesTab.tsx
import { useState } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getChangesPaginated, acknowledgeChange, exportChangesCSV, exportChangesExcel, downloadBlob } from '../api/monitoring';
import { FiChevronUp, FiChevronDown, FiSearch, FiDownload, FiRefreshCw, FiCheck, FiAlertTriangle, FiClock, FiFileText } from 'react-icons/fi';
import type { PaginatedChangesResponse } from '../api/types';
import styles from './ChangesTab.module.css';
import { ExpandableValue } from '../components/MonitoringProject/ExpandableValue';

import { logger } from '../utils/logger';

type ContextType = { refreshTrigger: boolean };

type SortField = 'ts' | 'url' | 'field' | 'severity' | 'change_type';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export default function ChangesTab() {
  const { id } = useParams<{ id: string }>();
  const { /* refreshTrigger */ } = useOutletContext<ContextType>(); // refreshTrigger not used
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [fieldFilter, setFieldFilter] = useState<string>('all');
  const [acknowledgmentFilter, setAcknowledgmentFilter] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'ts', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // Server-side paginated data fetching
  const { data, isLoading, isError, refetch } = useQuery<PaginatedChangesResponse>({
    queryKey: ['changes', id, currentPage, pageSize],
    queryFn: () => getChangesPaginated(id!, currentPage, pageSize),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    enabled: !!id,
  });

  // Extract data from paginated response
  const changes = data?.items || [];
  const totalItems = data?.total || 0;
  const totalPages = data?.total_pages || 1;

  // Acknowledge change mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (changeId: string) => acknowledgeChange(changeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changes', id] });
    },
  });

  // Client-side filtering on current page data
  const filteredData = changes.filter(change => {
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        change.url.toLowerCase().includes(term) ||
        change.field.toLowerCase().includes(term) ||
        change.before?.toLowerCase().includes(term) ||
        change.after?.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }

    // Apply severity filter
    if (severityFilter !== 'all' && change.severity !== severityFilter) return false;

    // Apply field filter
    if (fieldFilter !== 'all' && change.field !== fieldFilter) return false;

    // Apply acknowledgment filter
    if (acknowledgmentFilter === 'acknowledged' && !change.is_acknowledged) return false;
    if (acknowledgmentFilter === 'unacknowledged' && change.is_acknowledged) return false;

    return true;
  });

  // Get unique fields for filtering (from current page)
  const uniqueFields = [...new Set(changes.map(change => change.field))];

  // Reset to first page when page size changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAcknowledge = (changeId: string) => {
    acknowledgeMutation.mutate(changeId);
  };

  // Server-side Excel export (recommended)
  const handleExportExcel = async () => {
    if (!id || isExportingExcel) return;

    setIsExportingExcel(true);
    try {
      const blob = await exportChangesExcel(id);
      const filename = `changes-${id}-${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadBlob(blob, filename);
    } catch (error) {
      logger.error('Excel export failed:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  // Server-side CSV export
  const handleExportCSV = async () => {
    if (!id || isExportingCSV) return;

    setIsExportingCSV(true);
    try {
      const blob = await exportChangesCSV(id);
      const filename = `changes-${id}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadBlob(blob, filename);
    } catch (error) {
      logger.error('CSV export failed:', error);
    } finally {
      setIsExportingCSV(false);
    }
  };

  // Navigate to URL history page
  const handleViewURLHistory = (url: string) => {
    const encodedUrl = encodeURIComponent(url);
    navigate(`/monitoring/${id}/url-history?url=${encodedUrl}`);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'critical';
      case 'high': return 'high';
      case 'medium': return 'medium';
      case 'low': return 'low';
      default: return 'neutral';
    }
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'added': return '✓';
      case 'removed': return '✗';
      case 'modified': return '~';
      default: return '•';
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />;
  };

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <FiRefreshCw className={styles.loadingIcon} size={24} />
        <span>Loading changes...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorState}>
        <p>Error loading changes. Please try again.</p>
        <button onClick={() => refetch()} className={styles.retryButton}>
          <FiRefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  if (!changes.length) {
    return (
      <div className={styles.emptyState}>
        <p>No changes detected yet.</p>
        <p className={styles.emptyStateHint}>
          Changes will appear here after crawling and detecting modifications to your website.
        </p>
      </div>
    );
  }

  const stats = {
    total: totalItems,
    unacknowledged: filteredData.filter(c => !c.is_acknowledged).length,
    critical: filteredData.filter(c => c.severity === 'critical').length,
    high: filteredData.filter(c => c.severity === 'high').length,
  };

  return (
    <div className={styles.wrapper}>
      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FiSearch className={styles.searchIcon} size={16} />
          <input
            type="text"
            placeholder="Search in current page..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filters}>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={fieldFilter}
            onChange={(e) => setFieldFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Fields</option>
            {uniqueFields.map(field => (
              <option key={field} value={field}>{field}</option>
            ))}
          </select>

          <select
            value={acknowledgmentFilter}
            onChange={(e) => setAcknowledgmentFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Changes</option>
            <option value="unacknowledged">Unacknowledged</option>
            <option value="acknowledged">Acknowledged</option>
          </select>

          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className={styles.filterSelect}
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={200}>200 per page</option>
          </select>
        </div>

        <div className={styles.actions}>
          <button
            onClick={handleExportExcel}
            className={`${styles.exportButton} ${styles.exportExcel}`}
            disabled={isExportingExcel}
            title="Export to Excel (recommended)"
          >
            {isExportingExcel ? <FiRefreshCw size={16} className={styles.spinIcon} /> : <FiDownload size={16} />}
            {isExportingExcel ? 'Exporting...' : 'Excel'}
          </button>
          <button
            onClick={handleExportCSV}
            className={styles.exportButton}
            disabled={isExportingCSV}
            title="Export to CSV"
          >
            {isExportingCSV ? <FiRefreshCw size={16} className={styles.spinIcon} /> : <FiFileText size={16} />}
            {isExportingCSV ? 'Exporting...' : 'CSV'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        <span className={styles.statItem}>
          Showing {filteredData.length} of {changes.length} on this page
        </span>
        <span className={styles.statItem}>
          Total: {stats.total} changes
        </span>
        {stats.unacknowledged > 0 && (
          <span className={`${styles.statItem} ${styles.statAlert}`}>
            <FiAlertTriangle size={14} />
            {stats.unacknowledged} unacknowledged
          </span>
        )}
        {stats.critical > 0 && (
          <span className={`${styles.statItem} ${styles.statCritical}`}>
            {stats.critical} critical
          </span>
        )}
        {stats.high > 0 && (
          <span className={`${styles.statItem} ${styles.statHigh}`}>
            {stats.high} high
          </span>
        )}
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.headerCell} onClick={() => handleSort('ts')}>
                <div className={styles.headerContent}>
                  Date
                  <SortIcon field="ts" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('url')}>
                <div className={styles.headerContent}>
                  URL
                  <SortIcon field="url" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('field')}>
                <div className={styles.headerContent}>
                  Field
                  <SortIcon field="field" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('severity')}>
                <div className={styles.headerContent}>
                  Severity
                  <SortIcon field="severity" />
                </div>
              </th>
              <th className={styles.headerCell}>Before</th>
              <th className={styles.headerCell}>After</th>
              <th className={styles.headerCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((change, index) => (
              <tr key={`${change.url}-${change.ts}-${index}`} className={styles.dataRow}>
                <td className={styles.dataCell}>
                  <div className={styles.dateCell}>
                    {new Date(change.ts).toLocaleDateString()}
                    <span className={styles.timeText}>
                      {new Date(change.ts).toLocaleTimeString()}
                    </span>
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.urlCell}>
                    <a
                      href={change.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.urlLink}
                    >
                      {change.url}
                    </a>
                    <button
                      onClick={() => handleViewURLHistory(change.url)}
                      className={styles.historyButton}
                      title="View URL history & timeline"
                    >
                      <FiClock size={14} />
                      View History
                    </button>
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.fieldCell}>
                    <span className={styles.changeTypeIcon}>
                      {getChangeTypeIcon(change.change_type)}
                    </span>
                    {change.field}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <span className={`${styles.severityBadge} ${styles[getSeverityColor(change.severity)]}`}>
                    {change.severity}
                  </span>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.changeCell} title={change.before || ''}>
                    {change.before ? (
                      <ExpandableValue value={change.before} label="Before" />
                    ) : (
                      <span className={styles.emptyValue}>—</span>
                    )}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.changeCell} title={change.after || ''}>
                    {change.after ? (
                      <ExpandableValue value={change.after} label="After" />
                    ) : (
                      <span className={styles.emptyValue}>—</span>
                    )}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.actionsCell}>
                    {change.is_acknowledged ? (
                      <span className={styles.acknowledgedStatus}>
                        <FiCheck size={14} />
                        Acknowledged
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAcknowledge(change.id)}
                        className={styles.acknowledgeButton}
                        disabled={acknowledgeMutation.isPending}
                      >
                        <FiCheck size={14} />
                        Acknowledge
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Previous
          </button>
          
          <div className={styles.paginationInfo}>
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
